import type { Reading } from "./types";

const WORD_PATTERN = /\p{L}+(?:[’'-]\p{L}+)*/gu;

export function normalizeContentWord(value: string) {
  return value.toLocaleLowerCase().replace(/^[^\p{L}]+|[^\p{L}]+$/gu, "");
}

export function validateReading(reading: Reading) {
  const errors: string[] = [];
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(reading.id)) errors.push("id must be a stable slug");
  if (!/^\d{4}-\d{2}-\d{2}\.\d+$/.test(reading.edition)) errors.push("edition must be date.sequence");
  if (reading.taxonomyVersion !== "taxonomy_v1") errors.push("taxonomy version is unsupported");
  if (reading.provenance !== "Verbalore original") errors.push("provenance must be explicit");
  if (reading.segments.length < 8) errors.push("reading needs at least eight maintained segments");

  const segmentIds = new Set<string>();
  const corpus = reading.segments.map((segment) => {
    if (segmentIds.has(segment.id)) errors.push(`duplicate segment id ${segment.id}`);
    segmentIds.add(segment.id);
    return segment.text;
  }).join(" ");
  const corpusWords = new Set((corpus.match(WORD_PATTERN) || []).map(normalizeContentWord));

  for (const [key, entry] of Object.entries(reading.words)) {
    if (normalizeContentWord(entry.word) !== key) errors.push(`${key}: key and word disagree`);
    if (!corpusWords.has(key)) errors.push(`${key}: word does not occur in reading`);
    if (!corpus.includes(entry.sentence)) errors.push(`${key}: source sentence is not exact`);
    if (!entry.translations.English || !entry.translations.Hindi || !entry.translations.Punjabi) {
      errors.push(`${key}: all maintained translations are required`);
    }
  }

  for (const word of reading.exercise.targetWords) {
    if (!reading.words[normalizeContentWord(word)]) errors.push(`exercise target ${word} has no lexical entry`);
  }
  if (!reading.taxonomy.outcomes.length || !reading.taxonomy.rhetoricalMoves.length) {
    errors.push("taxonomy needs outcomes and rhetorical moves");
  }
  return errors;
}

export function assertValidCatalog(readings: Reading[]) {
  const ids = new Set<string>();
  for (const reading of readings) {
    if (ids.has(reading.id)) throw new Error(`Duplicate reading id: ${reading.id}`);
    ids.add(reading.id);
    const errors = validateReading(reading);
    if (errors.length) throw new Error(`${reading.id}: ${errors.join("; ")}`);
  }
  return readings;
}
